import { ModalWrapper } from '@shared/ui/modal-wrapper';
import stylex from '@stylexjs/stylex';
import { FC, useEffect, useState, useCallback } from 'react';
import { Button } from '@shared/ui/kit';
import { typography } from '@shared/tokens/typography.stylex';
import { IOrganizer } from '@features/organizer/model/types';
import { styles } from './styles';
import { fonts } from '@shared/tokens/fonts.stylex';
import { useFetchOrganizerOtp } from './model/useFetchOrganizerOtp';
import { CodeRow } from './codeRowOrganizer';

interface OrganizerCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizer?: IOrganizer;
  eventId?: number;
}

export const OrganizerCodeModal: FC<OrganizerCodeModalProps> = ({ isOpen, onClose, organizer, eventId }) => {
  const [showCode, setShowCode] = useState(false);
  const [autoShowTimer, setAutoShowTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const { data, fetching, error, reexecuteQuery } = useFetchOrganizerOtp(
    eventId && organizer?.id ? { eventId, userId: organizer.id } : null
  );

  useEffect(() => {
    return () => {
      if (autoShowTimer) clearTimeout(autoShowTimer);
    };
  }, [autoShowTimer]);

  useEffect(() => {
    if (isOpen) {
      setLocalError(null);
      setShowCode(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      setLocalError('Ошибка загрузки кода');
      setShowCode(true);
    }
  }, [error]);

  const handleClose = useCallback(() => {
    setShowCode(false);
    setLocalError(null);
    if (autoShowTimer) clearTimeout(autoShowTimer);
    onClose();
  }, [autoShowTimer, onClose]);

  const handleRetry = useCallback(() => {
    setLocalError(null);
    reexecuteQuery({ requestPolicy: 'network-only' });
  }, [reexecuteQuery]);

  const handleShowClick = useCallback(() => {
    if (autoShowTimer) {
      clearTimeout(autoShowTimer);
      setAutoShowTimer(null);
    }

    if (localError) {
      handleRetry();
      return;
    }

    if (data) {
      setShowCode(true);
    } else {
      handleRetry();
    }
  }, [autoShowTimer, localError, data, handleRetry]);

  useEffect(() => {
    if (isOpen && organizer && eventId) {
      setShowCode(false);
      setLocalError(null);

      const timer = setTimeout(() => {
        setShowCode(true);
      }, 10000);

      setAutoShowTimer(timer);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [isOpen, organizer?.id, eventId]);

  const renderCodeBlocks = useCallback(() => {
    if (showCode && localError) {
      return (
        <div {...stylex.props(styles.errorContainer)}>
          <div {...stylex.props(styles.rowContainer)}>
            <CodeRow isError />
            <CodeRow isError />
          </div>
          <div {...stylex.props(styles.errorText)}>Ошибка загрузки кода</div>
        </div>
      );
    }

    if (showCode && data?.getOrganizerOtp?.code) {
      const code = data.getOrganizerOtp.code;
      return (
        <>
          <CodeRow codePart={code.substring(0, 3)} />
          <CodeRow codePart={code.substring(3, 6)} />
        </>
      );
    }

    return (
      <>
        <CodeRow />
        <CodeRow />
      </>
    );
  }, [showCode, localError, data]);

  return (
    <ModalWrapper
      active={isOpen}
      settingsHeader={{ height: '16px', padding: '0px' }}
      settingsContainer={{
        height: 'auto',
        width: '480px',
        padding: '0px 24px',
      }}
      settingsFooter={{ height: '16px', padding: '0px' }}
    >
      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.title)}>Подтвердить действие</div>

        <p {...stylex.props(styles.text)}>
          Убедитесь, что рядом с вами нет посторонних лиц. Будет показан код для входа в мобильное приложение
        </p>

        <div {...stylex.props(styles.codeBlock)}>{renderCodeBlocks()}</div>

        <div {...stylex.props(styles.actions)}>
          <Button
            variant="secondary"
            size="small"
            onClick={handleClose}
            settings={{
              padding: '8px 20px',
              fontFamily: fonts.din2014Regular,
              fontSize: typography['fontSizeCaption-1'],
              lineHeight: typography['lineHeightCaption-1'],
            }}
          >
            Закрыть
          </Button>

          <Button
            variant="accent"
            size="small"
            onClick={handleShowClick}
            isDisabled={fetching}
            settings={{
              padding: '8px 20px',
              fontFamily: fonts.din2014Regular,
              fontSize: typography['fontSizeCaption-1'],
              lineHeight: typography['lineHeightCaption-1'],
            }}
          >
            {localError ? 'Обновить' : 'Показать'}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
